// Player avatar — full port of v1's detailed karate figure
// (js/player-manager.js:113-560): head with hair/eyes/nose, neck, gi jacket,
// belt with knot and hanging ends, jointed arm/leg groups with sleeves, gi
// pants and proper shoes. Proportions are v1's; the shoe sole sits 0.19
// below the origin, matching SHOE_BOTTOM_OFFSET in tuning.ts.

import * as THREE from 'three';

export interface Avatar {
  group: THREE.Group;
  leftArm: THREE.Object3D;
  rightArm: THREE.Object3D;
  leftLeg: THREE.Object3D;
  rightLeg: THREE.Object3D;
  belt: THREE.Mesh;
  giMaterial: THREE.MeshLambertMaterial;
  inner: THREE.Group;
  headband: THREE.Mesh | null;
}

export const GI_WHITE = 0xf5f5f0;
const SKIN = 0xe8b98a;
const HAIR = 0x3a2a1d;
const SHOE = 0xffffff;
const SOLE = 0x1a1a1a;

function addHair(group: THREE.Group, material: THREE.Material): void {
  // v1 createHair: main + back + side tufts, shaped for the rear camera
  const main = new THREE.Mesh(new THREE.SphereGeometry(0.18, 8, 6), material);
  main.position.set(0, 1.16, -0.02);
  main.scale.set(1.0, 0.75, 1.25);
  group.add(main);

  const back = new THREE.Mesh(new THREE.SphereGeometry(0.14, 6, 5), material);
  back.position.set(0, 1.08, -0.15);
  back.scale.set(1.1, 0.9, 1.4);
  group.add(back);

  const sideGeo = new THREE.SphereGeometry(0.05, 4, 3);
  for (const x of [-0.12, 0.12]) {
    const side = new THREE.Mesh(sideGeo, material);
    side.position.set(x, 1.15, -0.05);
    side.scale.set(0.8, 0.7, 1.0);
    group.add(side);
  }
}

function addFace(group: THREE.Group): void {
  // v1 createEyes + nose (visible during the combat camera zoom)
  const eyeGeo = new THREE.SphereGeometry(0.02, 4, 4);
  const pupilGeo = new THREE.SphereGeometry(0.012, 4, 4);
  const white = new THREE.MeshLambertMaterial({ color: 0xffffff });
  const black = new THREE.MeshLambertMaterial({ color: 0x000000 });
  for (const x of [-0.06, 0.06]) {
    const eye = new THREE.Mesh(eyeGeo, white);
    eye.position.set(x, 1.12, 0.16);
    group.add(eye);
    const pupil = new THREE.Mesh(pupilGeo, black);
    pupil.position.set(x, 1.12, 0.168);
    group.add(pupil);
  }
  const nose = new THREE.Mesh(
    new THREE.SphereGeometry(0.015, 4, 4),
    new THREE.MeshLambertMaterial({ color: SKIN }),
  );
  nose.position.set(0, 1.08, 0.17);
  group.add(nose);
}

function buildArm(gi: THREE.Material, skin: THREE.Material, side: -1 | 1): THREE.Group {
  // Pivot at the shoulder (v1 arm groups at ±0.22, 0.85)
  const arm = new THREE.Group();
  arm.position.set(side * 0.22, 0.85, 0);

  const sleeve = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.26, 0.13), gi);
  sleeve.position.y = -0.1;
  sleeve.castShadow = true;
  arm.add(sleeve);

  const forearm = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.2, 6), skin);
  forearm.position.y = -0.3;
  forearm.castShadow = true;
  arm.add(forearm);

  const hand = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 5), skin);
  hand.position.y = -0.42;
  arm.add(hand);

  return arm;
}

function buildLeg(
  gi: THREE.Material,
  skin: THREE.Material,
  shoeMats: [THREE.Material, THREE.Material],
  side: -1 | 1,
): THREE.Group {
  // Pivot at the hip (v1 leg groups at ±0.08, 0.3)
  const leg = new THREE.Group();
  leg.position.set(side * 0.08, 0.3, 0);

  // Gi pants over the thigh (v1 box style 0.18×0.32×0.16)
  const pants = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.32, 0.16), gi);
  pants.position.y = -0.2;
  pants.castShadow = true;
  leg.add(pants);

  const shin = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.045, 0.18, 8), skin);
  shin.position.y = -0.34;
  shin.castShadow = true;
  leg.add(shin);

  // Shoe: body + darker sole + toe cap (v1 createShoes)
  const shoe = new THREE.Group();
  shoe.position.y = -0.34;
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.08, 0.18), shoeMats[0]);
  body.position.set(0, -0.1, 0.06);
  shoe.add(body);
  const sole = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.02, 0.2), shoeMats[1]);
  sole.position.set(0, -0.15, 0.06);
  shoe.add(sole);
  const toeCap = new THREE.Mesh(new THREE.SphereGeometry(0.04, 8, 6), shoeMats[0]);
  toeCap.position.set(0, -0.1, 0.12);
  toeCap.scale.set(1.5, 0.8, 1.0);
  shoe.add(toeCap);
  leg.add(shoe);

  return leg;
}

export function buildAvatar(): Avatar {
  const group = new THREE.Group();
  const gi = new THREE.MeshLambertMaterial({ color: GI_WHITE });
  const skin = new THREE.MeshLambertMaterial({ color: SKIN });
  const hair = new THREE.MeshLambertMaterial({ color: HAIR });
  const beltMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
  const shoeMat = new THREE.MeshLambertMaterial({ color: SHOE });
  const soleMat = new THREE.MeshLambertMaterial({ color: SOLE });

  // Head + neck (v1: head sphere 0.18 at y 1.1)
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.18, 8, 6), skin);
  head.position.y = 1.1;
  head.castShadow = true;
  group.add(head);
  addHair(group, hair);
  addFace(group);
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.12, 6), skin);
  neck.position.y = 0.98;
  group.add(neck);

  // Gi jacket torso (v1: 0.4×0.6×0.25 at y 0.6)
  const torso = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.6, 0.25), gi);
  torso.position.y = 0.6;
  torso.castShadow = true;
  group.add(torso);

  // Belt (obi) with knot + hanging ends — one material so belt promotions
  // recolor everything together (v1 createBelt)
  const belt = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.08, 0.27), beltMat);
  belt.position.set(0, 0.5, 0);
  group.add(belt);
  const knot = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.08, 0.04), beltMat);
  knot.position.set(0, 0.5, 0.15);
  group.add(knot);
  const beltEndGeo = new THREE.BoxGeometry(0.03, 0.15, 0.02);
  for (const x of [-0.02, 0.02]) {
    const end = new THREE.Mesh(beltEndGeo, beltMat);
    end.position.set(x, 0.37, 0.15);
    end.rotation.x = 0.2;
    group.add(end);
  }

  const leftArm = buildArm(gi, skin, -1);
  const rightArm = buildArm(gi, skin, 1);
  const leftLeg = buildLeg(gi, skin, [shoeMat, soleMat], -1);
  const rightLeg = buildLeg(gi, skin, [shoeMat, soleMat], 1);
  group.add(leftArm, rightArm, leftLeg, rightLeg);

  // Face away from the camera (running into the screen, -z)
  group.rotation.y = Math.PI;
  const wrapper = new THREE.Group();
  wrapper.add(group);
  return {
    group: wrapper,
    leftArm,
    rightArm,
    leftLeg,
    rightLeg,
    belt,
    giMaterial: gi,
    inner: group,
    headband: null,
  };
}

/** Shop cosmetics (Phase 6): recolor the gi, add/remove a headband.
 *  The gi material is shared by jacket, sleeves and pants; reads it off the
 *  torso because the toon pipeline swaps materials after scene-add. */
export function applyCosmetics(
  avatar: Avatar,
  giColor: number | null,
  headbandColor: number | null,
): void {
  const sleeve = (avatar.leftArm as THREE.Group).children[0] as THREE.Mesh;
  const material = sleeve.material as THREE.MeshLambertMaterial | THREE.MeshToonMaterial;
  material.color.setHex(giColor ?? GI_WHITE);
  if (avatar.headband) {
    avatar.inner.remove(avatar.headband);
    avatar.headband = null;
  }
  if (headbandColor !== null) {
    const headband = new THREE.Mesh(
      new THREE.CylinderGeometry(0.185, 0.185, 0.05, 12),
      new THREE.MeshLambertMaterial({ color: headbandColor }),
    );
    headband.position.set(0, 1.14, 0);
    avatar.inner.add(headband);
    avatar.headband = headband;
  }
}

export function animateAvatar(
  avatar: Avatar,
  time: number,
  gameSpeed: number,
  isJumping: boolean,
  jumpVelocity: number,
): void {
  if (isJumping) {
    // Tucked pose scaled by ascent (v1 :766-807)
    const progress = Math.max(0, jumpVelocity / 0.32);
    avatar.leftLeg.rotation.x = -0.8 + progress * 0.4;
    avatar.rightLeg.rotation.x = 0.5 - progress * 0.3;
    avatar.leftArm.rotation.x = -0.6;
    avatar.rightArm.rotation.x = -0.6;
    return;
  }
  const cycle = time * (8 + gameSpeed * 40);
  const swing = Math.sin(cycle);
  avatar.leftLeg.rotation.x = swing * 0.9;
  avatar.rightLeg.rotation.x = -swing * 0.9;
  avatar.leftArm.rotation.x = -swing * 0.7;
  avatar.rightArm.rotation.x = swing * 0.7;
}
